import React from "react";
import EmployeesTableRow, {
  SPECIAL_ACCESS_LABELS,
  isAutoGeneratedEmail,
} from "./EmployeesTableRow";
import ImportEmployeesModal from "./ImportEmployeesModal";
import BatchWelcomeMessagesModal from "./BatchWelcomeMessagesModal";
import {
  API_get_certificates,
  API_send_welcome_message,
} from "../../../../../constants";
import { getRightworkAPI, postRightworkAPI } from "../../utilities/requests";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import Tooltip from "@mui/material/Tooltip";
import Drawer from "@mui/material/Drawer";
import DrawerHeader from "../../shared/helpers/DrawerHeader";
import DrawerFooterContainerLarge from "../../shared/helpers/DrawerFooterContainerLarge";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import AddIcon from "@mui/icons-material/Add";
import LoadingButton from "@mui/lab/LoadingButton";
import SaveIcon from "@mui/icons-material/Save";
import ButtonOutlined from "../../shared/helpers/ButtonOutlined";
import ButtonDelete from "../../shared/helpers/ButtonDelete";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import FiberPinIcon from "@mui/icons-material/FiberPin";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import { getInitials } from "../../utilities/helpers";
import { getCardColor2, isValidSMSPhone, clockPinEnabledForStore } from "../../../../../helpers/helpers";
import EmployeeProfile from "./EmployeeProfile";
import dayjs from "dayjs";
import { DemoContainer, DemoItem } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { formatDateReadable } from "../../utilities/helpers";
import WarningIcon from "@mui/icons-material/Warning";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import RolesCapabilitySelector from "../../../home/schedule_components/RolesCapabilitySelector";

const isAdminPermission = (permissionType) =>
  permissionType === "company_admin" ||
  permissionType === "rightwork_internal";

const getRosterDisplayPriority = (employee) => {
  const isHidden = Boolean(employee?.is_hidden);
  const isAdmin = isAdminPermission(employee?.permission_type);

  if (!isHidden && !isAdmin) return 0;
  if (!isHidden && isAdmin) return 1;
  if (isHidden && !isAdmin) return 2;
  return 3;
};

const orderRosterForDefaultView = (employeeList = []) =>
  [...(employeeList || [])]
    .map((employee, index) => ({ employee, index }))
    .sort((a, b) => {
      const priorityDiff =
        getRosterDisplayPriority(a.employee) -
        getRosterDisplayPriority(b.employee);
      if (priorityDiff !== 0) return priorityDiff;
      return a.index - b.index;
    })
    .map(({ employee }) => employee);

const mergeRosterSubsetOrder = (
  fullEmployeeList,
  currentSubset,
  reorderedSubset
) => {
  const subsetIds = new Set(currentSubset.map((employee) => employee.id));
  const reorderedQueue = [...reorderedSubset];

  return fullEmployeeList.map((employee) =>
    subsetIds.has(employee.id) ? reorderedQueue.shift() : employee
  );
};

export default function EmployeesTable(props) {
  const default_data = {
    first_name: "",
    last_name: "",
    email: "",
    phone: null,
    clock_pin: null,
    permission_type: "",
    stm_id: null,
    is_hidden: false,
    date_of_birth: null,
    date_of_hire: null,
    emergency_contact_name: null,
    emergency_contact_phone: null,
    emergency_contact_relationship: null,
  };

  const [open, setOpen] = React.useState(false);
  const [sendWelcomeEmail, setSendWelcomeEmail] = React.useState(true);
  const [welcomeChannel, setWelcomeChannel] = React.useState("email");

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setSendWelcomeEmail(true);
    setWelcomeChannel("email");
    {
      props.selectedTeamMemberData
        ? setModalData({
            first_name: props.selectedTeamMemberData?.first_name,
            last_name: props.selectedTeamMemberData?.last_name,
            email: props.selectedTeamMemberData?.email,
            phone: props.selectedTeamMemberData?.phone_number,
            clock_pin: props.selectedTeamMemberData?.clock_pin,
            permission_type: props.selectedTeamMemberData?.permission_type,
            stm_id: props.selectedTeamMemberData?.id,
            is_hidden: props.selectedTeamMemberData?.is_hidden,
            date_of_birth: props.selectedTeamMemberData?.date_of_birth,
            date_of_hire: props.selectedTeamMemberData?.date_of_hire,
            emergency_contact_name: props.selectedTeamMemberData?.emergency_contact_name,
            emergency_contact_phone: props.selectedTeamMemberData?.emergency_contact_phone,
            emergency_contact_relationship: props.selectedTeamMemberData?.emergency_contact_relationship,
          })
        : setModalData(default_data);
    }
    // Reset role state
    setRoleIds([]);
    setRoleRatings([]);
    setMainRoleId(null);
  };
  const [modalData, setModalData] = React.useState(default_data);
  const [modalEdit, setModalEdit] = React.useState(false);

  // Defense-in-depth: keep welcomeChannel coherent with phone state. If the
  // manager clears the phone or types an unsupported SMS number after picking
  // SMS, snap back to email so the SMS radio doesn't
  // show as "checked but disabled" and the backend doesn't have to silently
  // fall back. Must live below the modalData useState — its dependency array
  // reads modalData?.phone on every render, which would throw a temporal-
  // dead-zone ReferenceError if hoisted above.
  React.useEffect(() => {
    if (welcomeChannel === "sms" && !isValidSMSPhone(modalData?.phone)) {
      setWelcomeChannel("email");
    }
  }, [modalData?.phone, welcomeChannel]);

  // Role state for capability selector
  const [roleIds, setRoleIds] = React.useState([]);
  const [roleRatings, setRoleRatings] = React.useState([]);
  const [mainRoleId, setMainRoleId] = React.useState(null);

  // POS Modal state
  const [posModalOpen, setPosModalOpen] = React.useState(false);
  const [batchWelcomeModalOpen, setBatchWelcomeModalOpen] =
    React.useState(false);

  const handlePosModalOpen = () => setPosModalOpen(true);
  const handlePosModalClose = () => setPosModalOpen(false);
  const handleBatchWelcomeModalClose = () => setBatchWelcomeModalOpen(false);

  const [employees, setEmployees] = React.useState(() =>
    orderRosterForDefaultView(props.employees || [])
  );
  const [isReordered, setIsReordered] = React.useState(false);

  React.useEffect(() => {
    setEmployees(orderRosterForDefaultView(props.employees || []));
    setIsReordered(false);
  }, [props.employees, props.homeStore]);

  const toastLinkingAvailable =
    Boolean(props.posProvider) && !props.timeEntriesEnabled;
  // PIN column and editor: the time clock needs a PIN, and so does the task
  // kiosk when the store requires one there.
  const clockPinEnabled =
    !!props.timeEntriesEnabled || clockPinEnabledForStore(props.storeNames, props.homeStore);

  const handleToastLinkUpdate = React.useCallback((storeTeamMemberData) => {
    if (!storeTeamMemberData || !storeTeamMemberData.id) {
      return;
    }
    setEmployees((prev) => {
      if (!Array.isArray(prev)) {
        return prev;
      }
      return prev.map((employee) =>
        employee.id === storeTeamMemberData.id
          ? {
              ...employee,
              external_toast_employee_guid:
                storeTeamMemberData.external_toast_employee_guid,
              pos_store_team_member_uuid:
                storeTeamMemberData.pos_store_team_member_uuid,
            }
          : employee
      );
    });
  }, []);

  const handleToastLinkRefresh = React.useCallback(
    (memberId) => {
      if (typeof props.refreshEmployees === "function") {
        props.refreshEmployees();
      }
      if (
        typeof props.fetchStoreTeamMember === "function" &&
        memberId
      ) {
        props.fetchStoreTeamMember(false, memberId);
      }
    },
    [props.refreshEmployees, props.fetchStoreTeamMember]
  );

  const handleOpenToastModal = React.useCallback(
    (member) => {
      if (!toastLinkingAvailable || !props.onOpenToastModal) {
        return;
      }
      if (!member || !member.id) {
        return;
      }
      const normalizedMember = { ...member, is_archived: false };
      props.onOpenToastModal(normalizedMember, {
        onToastLinkUpdate: handleToastLinkUpdate,
        onRequestRefresh: () => handleToastLinkRefresh(member.id),
      });
    },
    [
      toastLinkingAvailable,
      props.onOpenToastModal,
      handleToastLinkUpdate,
      handleToastLinkRefresh,
    ]
  );

  React.useEffect(() => {
    props.setSelectedTeamMemberId(props.selectedTeamMemberData?.id);
    setModalData({
      first_name: props.selectedTeamMemberData?.first_name,
      last_name: props.selectedTeamMemberData?.last_name,
      email: props.selectedTeamMemberData?.email,
      phone: props.selectedTeamMemberData?.phone_number,
      clock_pin: props.selectedTeamMemberData?.clock_pin,
      permission_type: props.selectedTeamMemberData?.permission_type,
      stm_id: props.selectedTeamMemberData?.id,
      is_hidden: props.selectedTeamMemberData?.is_hidden,
      date_of_birth: props.selectedTeamMemberData?.date_of_birth,
      date_of_hire: props.selectedTeamMemberData?.date_of_hire,
      emergency_contact_name: props.selectedTeamMemberData?.emergency_contact_name,
      emergency_contact_phone: props.selectedTeamMemberData?.emergency_contact_phone,
      emergency_contact_relationship: props.selectedTeamMemberData?.emergency_contact_relationship,
    });
  }, [props.selectedTeamMemberData]);

  // Initialize role state when modal opens
  React.useEffect(() => {
    if (open && !modalEdit) {
      // Reset role state for new employee creation
      setRoleIds([]);
      setRoleRatings(get_role_ratings());
      setMainRoleId(null);
    }
  }, [open, modalEdit, props.roleNames]);

  const [searchInput, setSearchInput] = React.useState("");
  const [showHiddenEmployees, setShowHiddenEmployees] = React.useState(false);
  const [autoGeneratedOnly, setAutoGeneratedOnly] = React.useState(false);

  const rosterScopeEmployees = React.useMemo(() => {
    const employeeList = employees || [];
    return showHiddenEmployees
      ? employeeList
      : employeeList.filter((employee) => !employee?.is_hidden);
  }, [employees, showHiddenEmployees]);

  const autoGeneratedEmployees = React.useMemo(
    () => rosterScopeEmployees?.filter((e) => isAutoGeneratedEmail(e.email)),
    [rosterScopeEmployees]
  );
  const autoGeneratedCount = autoGeneratedEmployees
    ? autoGeneratedEmployees.length
    : 0;
  const notLoggedInEmployees = React.useMemo(
    () =>
      rosterScopeEmployees?.filter(
        (e) => !e.user?.has_logged_in && !isAutoGeneratedEmail(e.email)
      ),
    [rosterScopeEmployees]
  );
  const notLoggedInCount = notLoggedInEmployees
    ? notLoggedInEmployees.length
    : 0;

  React.useEffect(() => {
    if (autoGeneratedOnly && autoGeneratedCount === 0) {
      setAutoGeneratedOnly(false);
    }
  }, [autoGeneratedOnly, autoGeneratedCount]);

  const onDragEnd = (result) => {
    if (!result.destination) return; // No destination, do nothing
    if (reorderDisabled) return;

    const currentDisplayedEmployees = displayedEmployees || [];
    const reorderedDisplayedEmployees = Array.from(currentDisplayedEmployees);
    const [reorderedItem] = reorderedDisplayedEmployees.splice(
      result.source.index,
      1
    ); // Remove the dragged item from its original position
    reorderedDisplayedEmployees.splice(
      result.destination.index,
      0,
      reorderedItem
    ); // Insert the dragged item into its new position

    if (showHiddenEmployees) {
      setEmployees(reorderedDisplayedEmployees);
    } else {
      setEmployees((prevEmployees) =>
        mergeRosterSubsetOrder(
          prevEmployees,
          currentDisplayedEmployees,
          reorderedDisplayedEmployees
        )
      );
    }

    setIsReordered(true);
  };

  const handleSaveOrder = () => {
    const reorderedData = employees.map((employee, index) => ({
      id: employee.id,
      sort_index: index,
    }));
    props.store_team_member_set_order(reorderedData);
    setIsReordered(false);
  };

  const handleDateChange = (newValue) => {
    const formattedDate = newValue ? newValue.format("YYYY-MM-DD") : "";
    setModalData((prevData) => ({
      ...prevData,
      date_of_birth: formattedDate,
    }));
  };

  const handleDateOfHireChange = (newValue) => {
    const formattedDate = newValue ? newValue.format("YYYY-MM-DD") : "";
    setModalData((prevData) => ({
      ...prevData,
      date_of_hire: formattedDate,
    }));
  };

  function handleChange(evt) {
    const { name, value } = evt.target;
    setModalData({
      ...modalData,
      [name]: value,
    });
  }

  // Role handling functions
  function get_role_ratings() {
    const role_names = props.roleNames || [];
    return role_names.map((obj) => ({
      ...obj,
      proficiency_rating: 3,
      training_proficiency_rating: 0,
    }));
  }

  const handleRoleChange = (event) => {
    const {
      target: { value },
    } = event;
    setRoleIds(
      // On autofill we get a stringified value.
      typeof value === "string" ? value.split(",") : value
    );
  };

  const getStars = (role_id) => {
    const stars = roleRatings.filter((r) => r.id === role_id);
    if (props.proficiencyFromLMSEnabled) {
      if (stars.length > 0) {
        return stars[0]["training_proficiency_rating"];
      } else {
        return 0;
      }
    } else {
      if (stars.length > 0) {
        return stars[0]["proficiency_rating"];
      } else {
        return 3;
      }
    }
  };

  const getTrainingProficiency = (role_id) => {
    const stars = roleRatings.filter((r) => r.id === role_id);
    if (stars.length > 0) {
      return stars[0]["training_proficiency_rating"] || 0;
    }
    return 0;
  };

  const getProficiencyRating = (role_id) => {
    const stars = roleRatings.filter((r) => r.id === role_id);
    if (stars.length > 0) {
      return stars[0]["proficiency_rating"] || 3;
    }
    return 3;
  };

  function update_role_proficiency(roleId, value) {
    const data = [...roleRatings];
    const new_data = data.map((obj) =>
      obj.id === roleId ? { ...obj, proficiency_rating: value } : obj
    );
    setRoleRatings(new_data);
  }

  async function handleSubmit() {
    const prevEmail = props.selectedTeamMemberData?.email;
    const wasAuto = isAutoGeneratedEmail(prevEmail);
    const shouldSendWelcomeEmail = !modalEdit && sendWelcomeEmail;
    // Defensive: if phone is missing or invalid format after SMS was picked,
    // fall back to email so the backend doesn't have to silently override.
    // (The backend SMS normalizer would reject and fall back anyway,
    // but doing it here means the manager's submission matches their intent.)
    const effectiveWelcomeChannel =
      welcomeChannel === "sms" && !isValidSMSPhone(modalData.phone)
        ? "email"
        : welcomeChannel;

    // Build available_role_info array only when creating a new employee
    let available_role_info = null;
    if (!modalEdit) {
      available_role_info = [];
      roleIds.forEach((id) => {
        let dict = { scheduled_role: id };
        if (modalData.stm_id) {
          dict["store_team_member"] = modalData.stm_id;
        }
        dict["main_role_ind"] = id === mainRoleId ? 1 : 0;
        // Always preserve both rating types to avoid data loss
        dict["proficiency_rating"] = getProficiencyRating(id);
        dict["training_proficiency_rating"] = getTrainingProficiency(id);
        available_role_info.push(dict);
      });
    }

    try {
      /* 1️⃣ wait for the save to finish */
      const saved = await props.post_store_team_member(
        modalData.first_name,
        modalData.last_name,
        modalData.email,
        modalData.phone,
        modalData.permission_type,
        modalData.stm_id,
        modalData.clock_pin,
        modalData.date_of_birth,
        modalData.date_of_hire,
        shouldSendWelcomeEmail,
        available_role_info,
        null, // can_request_pto
        null, // can_request_sicktime
        modalData.emergency_contact_name,
        modalData.emergency_contact_phone,
        modalData.emergency_contact_relationship,
        effectiveWelcomeChannel
      );

      /* 2️⃣ then (and only then) decide about the welcome email */
      if (
        modalEdit &&
        wasAuto &&
        !isAutoGeneratedEmail(modalData.email) &&
        window.confirm(`Send welcome email to ${modalData.email}?`)
      ) {
        await postRightworkAPI(API_send_welcome_message, {
          store_id: props.homeStore,
          stm_id: modalData.stm_id, // use the id that actually got saved
        });
      }

      handleClose(); // 3️⃣ close the modal on success
    } catch (err) {
      console.error("Error saving employee", err);
      // show toast / alert here if you like
    }
  }

  let permissionInfo = {
    label: "",
    style: "",
  };
  if (props.selectedTeamMemberData?.permission_type === "company_admin") {
    permissionInfo = {
      label: "Admin",
      style: "border border-violet-600 bg-violet-50 text-violet-600",
    };
  } else if (
    props.selectedTeamMemberData?.permission_type === "company_manager"
  ) {
    permissionInfo = {
      label: "Manager",
      style: "border border-orange-600 bg-orange-50 text-orange-600",
    };
  } else if (
    props.selectedTeamMemberData?.permission_type === "company_timeclock"
  ) {
    permissionInfo = {
      label: "Time Clock",
      style: "border border-amber-600 bg-amber-50 text-amber-700",
    };
  } else {
    permissionInfo = {
      label: "Employee",
      style: "border border-blue-600 bg-blue-50 text-blue-600",
    };
  }

  const avatarCardColor = props.selectedTeamMemberData?.primary_role_id
    ? getCardColor2(
        props.selectedTeamMemberData?.primary_role_id,
        props.roleNames,
        false
      )
    : "bg-slate-500";

  // Code to show date picker
  const [showDailyDatePicker, setShowDailyDatePicker] = React.useState(false);
  const [showDateOfHirePicker, setShowDateOfHirePicker] = React.useState(false);
  const datePickerRef = React.useRef(null);
  const dateOfHirePickerRef = React.useRef(null);

  function toggleDailyDatePicker() {
    setShowDailyDatePicker((prevState) => !prevState);
  }

  function toggleDateOfHirePicker() {
    setShowDateOfHirePicker((prevState) => !prevState);
  }

  React.useEffect(() => {
    // This function checks if the clicked target is inside the date picker component
    function handleClickOutside(event) {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target)
      ) {
        setShowDailyDatePicker(false);
      }
      if (
        dateOfHirePickerRef.current &&
        !dateOfHirePickerRef.current.contains(event.target)
      ) {
        setShowDateOfHirePicker(false);
      }
    }

    // Add the event listener to the document when the component is mounted
    if (showDailyDatePicker || showDateOfHirePicker) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    // Clean up the event listener when the component is unmounted or the effect re-runs
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDailyDatePicker, showDateOfHirePicker]);

  const getPermissionLabel = (permission_type) => {
    if (permission_type === "company_admin") return "Admin";
    if (permission_type === "company_manager") return "Manager";
    if (permission_type === "company_timeclock") return "Time Clock";
    return "Employee";
  };

  const hiddenEmployeeCount = React.useMemo(
    () => (employees || []).filter((employee) => employee?.is_hidden).length,
    [employees]
  );

  // Searching spans the full roster, hidden included: hidden members (e.g.
  // auto-hidden admins) are otherwise unfindable without the Show Hidden
  // toggle, and an explicit name search is a clear signal the user wants
  // that person. Matches render with the existing "hidden" badge.
  const searchScopeEmployees = searchInput.trim()
    ? employees || []
    : rosterScopeEmployees;

  const filteredEmployees = searchScopeEmployees?.filter((employee) => {
    if (autoGeneratedOnly && !isAutoGeneratedEmail(employee?.email)) {
      return false;
    }

    const searchParts = searchInput?.toLowerCase()?.split(" ");
    const permissionLabel = getPermissionLabel(
      employee?.permission_type
    ).toLowerCase();
    const overrideLabels = (employee?.permission_type_overrides || [])
      .map((key) => SPECIAL_ACCESS_LABELS[key]?.toLowerCase())
      .filter(Boolean);
    return searchParts?.every(
      (part) =>
        employee?.first_name?.toLowerCase()?.includes(part) ||
        employee?.last_name?.toLowerCase()?.includes(part) ||
        employee?.email?.toLowerCase()?.includes(part) ||
        permissionLabel.includes(part) ||
        overrideLabels.some((label) => label.includes(part))
    );
  });

  const [sortConfig, setSortConfig] = React.useState({
    column: null,
    direction: "asc",
  });
  const isSorted = sortConfig.column !== null;
  const isSearchFiltered = searchInput.trim().length > 0;
  const reorderDisabled = isSorted || isSearchFiltered || autoGeneratedOnly;

  const reorderDisabledTooltip = isSorted
    ? "Clear the sort to reorder employees."
    : isSearchFiltered
      ? "Clear the search to reorder employees."
      : autoGeneratedOnly
        ? "Clear the profile filter to reorder employees."
        : "You can drag and drop employees to reorder how they appear in the weekly scheduling view.";

  const handleSort = (column) => {
    setSortConfig((prev) => {
      if (prev.column !== column) return { column, direction: "asc" };
      if (prev.direction === "asc") return { column, direction: "desc" };
      return { column: null, direction: "asc" };
    });
  };

  const displayedEmployees = React.useMemo(() => {
    if (!filteredEmployees || !sortConfig.column) return filteredEmployees;
    const sorted = [...filteredEmployees].sort((a, b) => {
      const priorityDiff =
        getRosterDisplayPriority(a) - getRosterDisplayPriority(b);
      if (priorityDiff !== 0) return priorityDiff;

      let aVal = "";
      let bVal = "";
      if (sortConfig.column === "first_name") {
        aVal = (a.first_name || "").toLowerCase();
        bVal = (b.first_name || "").toLowerCase();
      } else if (sortConfig.column === "last_name") {
        aVal = (a.last_name || "").toLowerCase();
        bVal = (b.last_name || "").toLowerCase();
      } else if (sortConfig.column === "permission") {
        aVal = getPermissionLabel(a.permission_type).toLowerCase();
        bVal = getPermissionLabel(b.permission_type).toLowerCase();
      }
      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredEmployees, sortConfig]);

  const displayedEmployeeCount = displayedEmployees?.length || 0;

  const renderSortIcon = (column) => {
    if (sortConfig.column !== column) {
      return (
        <UnfoldMoreIcon
          style={{ fontSize: "14px" }}
          className="text-slate-300"
        />
      );
    }
    return sortConfig.direction === "asc" ? (
      <ArrowUpwardIcon
        style={{ fontSize: "14px" }}
        className="text-violet-700"
      />
    ) : (
      <ArrowDownwardIcon
        style={{ fontSize: "14px" }}
        className="text-violet-700"
      />
    );
  };

  return (
    <>
      {!props.showUserProfile ? (
        <div className="mt-4" data-help-id="team-members-table">
          <DragDropContext onDragEnd={onDragEnd}>
            <div className="flex flex-wrap items-end justify-between gap-4 px-6">
              <div className="flex flex-wrap items-end gap-4">
                <div>
                  <p className="text-slate-500 text-sm">
                    Search by name, email, permission, or access
                  </p>
                  <div className="mt-1 flex flex-wrap items-center gap-3">
                    <div className="relative">
                      <SearchIcon
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"
                        style={{ fontSize: "18px" }}
                      />
                      <input
                        className="w-[280px] border border-gray-300 pl-9 pr-3 h-[40px] text-sm rounded-lg outline-none transition-all duration-200 focus:border focus:border-violet-700 focus:shadow-sm"
                        type="text"
                        placeholder="Search employees"
                        value={searchInput}
                        onChange={(event) =>
                          setSearchInput(event.target.value)
                        }
                      />
                    </div>
                    {hiddenEmployeeCount > 0 && (
                      <button
                        type="button"
                        className={`h-10 rounded-lg border px-2.5 pr-3 text-sm font-medium transition-colors flex items-center ${
                          showHiddenEmployees
                            ? "border-violet-300 bg-violet-50 text-violet-700"
                            : "border-gray-300 bg-white text-slate-600 hover:border-slate-400 hover:text-slate-900"
                        }`}
                        onClick={() =>
                          setShowHiddenEmployees((prev) => !prev)
                        }
                      >
                        <Checkbox
                          size="small"
                          checked={showHiddenEmployees}
                          onChange={(event) =>
                            setShowHiddenEmployees(event.target.checked)
                          }
                          onClick={(event) => event.stopPropagation()}
                          sx={{
                            padding: "4px",
                            color: "#94a3b8",
                            "&.Mui-checked": { color: "#6d28d9" },
                          }}
                        />
                        <span className="ml-1">Show hidden</span>
                        <span
                          className={`ml-2 rounded-full px-1.5 py-0.5 text-xs ${
                            showHiddenEmployees
                              ? "bg-violet-100 text-violet-700"
                              : "bg-slate-100 text-slate-500"
                          }`}
                        >
                          {hiddenEmployeeCount}
                        </span>
                      </button>
                    )}
                  </div>
                  {autoGeneratedOnly && (
                    <div className="mt-2 flex items-center gap-2">
                      <div className="inline-flex items-center rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-medium text-rose-700">
                        Needs real email
                        <button
                          type="button"
                          className="ml-2 text-rose-500 hover:text-rose-700"
                          onClick={() => setAutoGeneratedOnly(false)}
                        >
                          <CloseIcon style={{ fontSize: "14px" }} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <Tooltip title="Add employee">
                <div
                  data-help-id="add-employee-button"
                  className="h-10 w-10 rounded-full bg-violet-700 hover:bg-violet-800 text-white text-whiten flex items-center justify-center cursor-pointer"
                  onClick={() => {
                    setModalData(default_data);
                    setModalEdit(false);
                    setSendWelcomeEmail(true);
                    handleOpen();
                  }}
                >
                  <AddIcon />
                </div>
              </Tooltip>
            </div>
            {!props.showUserProfile &&
              (notLoggedInCount > 0 || autoGeneratedCount > 0 || props.posEmployees?.length > 0) && (
                <div className="mx-6 mt-4 flex flex-wrap items-center gap-2 text-sm">
                  {notLoggedInCount > 0 && (
                    <button
                      type="button"
                      className="inline-flex h-9 items-center gap-2 rounded-lg border border-orange-200 bg-orange-50 px-3 text-orange-700 transition-colors hover:border-orange-300 hover:bg-orange-100"
                      onClick={() => setBatchWelcomeModalOpen(true)}
                    >
                      <WarningAmberIcon style={{ fontSize: "17px" }} />
                      <span className="font-medium">{`${notLoggedInCount} users haven't logged in`}</span>
                      <span className="rounded-full bg-white/80 px-2 py-0.5 text-xs font-medium text-orange-700">
                        Send invites
                      </span>
                    </button>
                  )}
                  {autoGeneratedCount > 0 && (
                    <button
                      type="button"
                      className={`inline-flex h-9 items-center gap-2 rounded-lg border px-3 transition-colors ${
                        autoGeneratedOnly
                          ? "border-rose-300 bg-rose-100 text-rose-800"
                          : "border-rose-200 bg-rose-50 text-rose-700 hover:border-rose-300 hover:bg-rose-100"
                      }`}
                      onClick={() => setAutoGeneratedOnly((prev) => !prev)}
                    >
                      <WarningIcon style={{ fontSize: "17px" }} />
                      <span className="font-medium">{`${autoGeneratedCount} auto-generated profiles need real emails`}</span>
                    </button>
                  )}
                  {props.posEmployees?.length > 0 && (
                    <div className="inline-flex h-9 items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 text-amber-700">
                      <WarningIcon style={{ fontSize: "17px" }} />
                      <span className="font-medium">{`${props.posEmployees?.length} employees not imported from POS`}</span>
                      <button
                        type="button"
                        className="rounded-full bg-white/80 px-2 py-0.5 text-xs font-medium text-amber-700 hover:bg-white"
                        onClick={handlePosModalOpen}
                      >
                        Review
                      </button>
                    </div>
                  )}
                </div>
              )}
            <div className="mt-4">
              <div className="mt-4 flex flex-col">
                <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8 min-w-full">
                  <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                    <div className="overflow-hidden">
                      <table className="min-w-full divide-y divide-slate-200">
                        <thead>
                          <tr>
                            <th
                              scope="col"
                              className="w-[4%] py-3.5 pl-6 pr-3 text-left text-sm text-slate-500 font-medium"
                            >
                              <span className="sr-only">Drag Icon</span>
                            </th>
                            <th
                              scope="col"
                              className="w-[8%] px-3 py-3.5 text-left text-sm text-slate-500 font-medium"
                            >
                              <span className="sr-only">Avatar</span>
                            </th>
                            <th
                              scope="col"
                              className="w-[10%] px-3 py-3.5 text-left text-sm text-slate-500 font-medium"
                            >
                              <div
                                className="inline-flex items-center gap-1 cursor-pointer select-none hover:text-slate-700"
                                onClick={() => handleSort("first_name")}
                              >
                                <span>First</span>
                                {renderSortIcon("first_name")}
                              </div>
                            </th>
                            <th
                              scope="col"
                              className="w-[14%] px-3 py-3.5 text-left text-sm text-slate-500 font-medium"
                            >
                              <div
                                className="inline-flex items-center gap-1 cursor-pointer select-none hover:text-slate-700"
                                onClick={() => handleSort("last_name")}
                              >
                                <span>Last</span>
                                {renderSortIcon("last_name")}
                              </div>
                            </th>
                            <th
                              scope="col"
                              className="w-[20%] px-3 py-3.5 text-left text-sm text-slate-500 font-medium"
                            >
                              Contact
                            </th>
                            {clockPinEnabled && (
                              <th
                                scope="col"
                                className="w-[6%] px-3 py-3.5 text-left text-sm text-slate-500 font-medium"
                              >
                                Pin
                              </th>
                            )}
                            <th
                              scope="col"
                              className="w-[18%] px-3 py-3.5 text-left text-sm text-slate-500 font-medium"
                            >
                              <div
                                className="inline-flex items-center gap-1 cursor-pointer select-none hover:text-slate-700"
                                onClick={() => handleSort("permission")}
                              >
                                <span>Permission</span>
                                {renderSortIcon("permission")}
                              </div>
                            </th>
                            <th
                              scope="col"
                              className="w-[12%] relative py-3.5 pl-3 pr-6 text-end text-sm font-medium"
                            >
                              {!reorderDisabled && isReordered ? (
                                <Tooltip title="Save this order of employees as default. If saved, employees will show up in this order in the weekly scheduling view (unless sorted or filtered otherwise).">
                                  <div className="w-full flex justify-end">
                                    <div
                                      className="px-5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full cursor-pointer"
                                      onClick={handleSaveOrder}
                                    >
                                      Save Order
                                    </div>
                                  </div>
                                </Tooltip>
                              ) : (
                                <Tooltip title={reorderDisabledTooltip}>
                                  <div className="w-full flex justify-end">
                                    <div className="px-4 py-1.5 bg-slate-200 text-slate-400 rounded-full">
                                      Save Order
                                    </div>
                                  </div>
                                </Tooltip>
                              )}
                            </th>
                          </tr>
                        </thead>
                        <Droppable droppableId="employees">
                          {(provided) => (
                            <tbody
                              className="divide-y divide-slate-200 bg-white"
                              {...provided.droppableProps}
                              ref={provided.innerRef}
                            >
                              {displayedEmployees?.map((person, index) => (
                                <Draggable
                                  key={String(person.id)}
                                  draggableId={String(person.id)}
                                  index={index}
                                  isDragDisabled={reorderDisabled}
                                >
                                  {(provided, snapshot) => (
                                    <EmployeesTableRow
                                      key={person.id}
                                      provided={provided}
                                      snapshot={snapshot}
                                      hideDragHandle={reorderDisabled}
                                      person={person}
                                      hasToastLink={Boolean(
                                        person.pos_store_team_member_uuid ||
                                          person.external_toast_employee_guid
                                      )}
                                      onOpenToastLink={
                                        toastLinkingAvailable
                                          ? handleOpenToastModal
                                          : null
                                      }
                                      id={person.id}
                                      firstName={person.first_name}
                                      lastName={person.last_name}
                                      email={person.email}
                                      phoneNumber={person.phone_number}
                                      is_hidden={person.is_hidden}
                                      clockPin={person.clock_pin}
                                      is_minor={person.is_minor}
                                      permissionType={person.permission_type}
                                      permissionTypeOverrides={
                                        person.permission_type_overrides
                                      }
                                      has_logged_in={person.user?.has_logged_in}
                                      welcomeSendCount={person.welcome_send_count}
                                      hide_store_team_member={
                                        props.hide_store_team_member
                                      }
                                      primaryRoleId={person.primary_role_id}
                                      roleNames={props.roleNames}
                                      open={open}
                                      handleOpen={handleOpen}
                                      handleClose={handleClose}
                                      setModalData={setModalData}
                                      setModalEdit={setModalEdit}
                                      timeEntriesEnabled={
                                        props.timeEntriesEnabled
                                      }
                                      clockPinEnabled={clockPinEnabled}
                                      setShowUserProfile={
                                        props.setShowUserProfile
                                      }
                                      homeStore={props.homeStore}
                                      fetchStoreTeamMember={
                                        props.fetchStoreTeamMember
                                      }
                                      refreshEmployees={props.refreshEmployees}
                                      posProvider={props.posProvider}
                                    />
                                  )}
                                </Draggable>
                              ))}
                              {displayedEmployeeCount === 0 && (
                                <tr>
                                  <td
                                    colSpan={clockPinEnabled ? 9 : 8}
                                    className="px-6 py-10 text-center text-sm text-slate-500"
                                  >
                                    No employees match this view.
                                  </td>
                                </tr>
                              )}
                              {provided.placeholder}
                            </tbody>
                          )}
                        </Droppable>
                      </table>
                      <div className="border-b border-slate-200"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </DragDropContext>
        </div>
      ) : (
        <div>
          <EmployeeProfile
            profileBackOverride={props.profileBackOverride}
            selectedTeamMemberLoadFailed={props.selectedTeamMemberLoadFailed}
            retrySelectedTeamMember={props.retrySelectedTeamMember}
            selectedTeamMemberData={props.selectedTeamMemberData}
            schedulingEnabled={props.schedulingEnabled}
            selectedTeamMemberAvailabilityData={
              props.selectedTeamMemberAvailabilityData
            }
            relatedTeamMembersData={props.relatedTeamMembersData}
            avatarCardColor={avatarCardColor}
            permissionInfo={permissionInfo}
            timeEntriesEnabled={props.timeEntriesEnabled}
            roleNames={props.roleNames}
            certificateData={props.selectedTeamMemberData?.user?.certificates}
            handleOpen={handleOpen}
            setModalEdit={setModalEdit}
            homeStore={props.homeStore}
            storeNames={props.storeNames}
            storeOptions={props.storeOptions}
            change_home_store={props.change_home_store}
            updatePersonInfo={props.updatePersonInfo}
            post_certificate={props.post_certificate}
            delete_certificate={props.delete_certificate}
            archive_store_team_member={props.archive_store_team_member}
            setShowUserProfile={props.setShowUserProfile}
            post_logbook_employee_entry={props.post_logbook_employee_entry}
            delete_logbook_employee_entry={props.delete_logbook_employee_entry}
            get_file_upload={props.get_file_upload}
            homeCompany={props.homeCompany}
            storeList={props.storeList}
            clone_or_reactivate_store_team_member={
              props.clone_or_reactivate_store_team_member
            }
            post_store_team_member_only_permission_type_and_store_id={
              props.post_store_team_member_only_permission_type_and_store_id
            }
            bulk_update_store_team_member_permissions={
              props.bulk_update_store_team_member_permissions
            }
            create_availability_request={props.create_availability_request}
            manager_rescind_availability_request={props.manager_rescind_availability_request}
            proficiencyStarsCount={props.proficiencyStarsCount}
            proficiencyFromLMSEnabled={props.proficiencyFromLMSEnabled}
            logbookEnabled={props.logbookEnabled}
            checkInsEnabled={props.checkInsEnabled}
            maintenanceEnabled={props.maintenanceEnabled}
            itTicketsEnabled={props.itTicketsEnabled}
            marketingTicketsEnabled={props.marketingTicketsEnabled}
            menuTicketsEnabled={props.menuTicketsEnabled}
            permissionRole={props.permissionRole}
            isCompanyAdmin={props.isCompanyAdmin}
            fetchStoreTeamMember={props.fetchStoreTeamMember}
            wagesInRightworkEnabled={props.wagesInRightworkEnabled}
            post_store_team_member={props.post_store_team_member}
            selectedTeamMemberTimeOffData={props.selectedTeamMemberTimeOffData}
            request_time_off_for_employee={props.request_time_off_for_employee}
            rescind_time_off_request={props.rescind_time_off_request}
          />
        </div>
      )}

      {/* Import Employees Modal */}
      <ImportEmployeesModal
        open={posModalOpen}
        onClose={handlePosModalClose}
        posEmployees={props.posEmployees}
        storeTeamMemberLookup={props.storeTeamMemberLookup}
        import_store_team_members_from_pos={props.import_store_team_members_from_pos}
        link_pos_store_team_member={props.link_pos_store_team_member}
        archive_pos_store_team_member={props.archive_pos_store_team_member}
      />

      <BatchWelcomeMessagesModal
        open={batchWelcomeModalOpen}
        onClose={handleBatchWelcomeModalClose}
        employees={notLoggedInEmployees || []}
        homeStore={props.homeStore}
        onInvitesSent={props.refreshEmployees}
      />

      {/* Original Employee Modal */}
      <Drawer anchor="right" open={open} onClose={handleClose}>
        <div className="w-[450px] h-full bg-white relative">
          <div className="max-h-screen overflow-auto">
            <DrawerHeader
              title={modalEdit ? "Edit Employee Info" : "Add Employee"}
              onClose={handleClose}
            />
            <div className="px-6">
              <div className="mt-4">
                <p className="text-sm text-slate-500">First Name*</p>
                <div className="mt-1">
                  <TextField
                    placeholder="John"
                    fullWidth
                    variant="outlined"
                    size="small"
                    name="first_name"
                    value={modalData?.first_name}
                    onChange={handleChange}
                    InputProps={{
                      sx: {
                        backgroundColor: "white",
                        borderRadius: "8px",
                        padding: "5px",
                        height: "45px",
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#6d28d9",
                        },
                      },
                    }}
                  />
                </div>
              </div>
              <div className="mt-4">
                <p className="text-sm text-slate-500">Last Name*</p>
                <div className="mt-1">
                  <TextField
                    placeholder="Smith"
                    fullWidth
                    variant="outlined"
                    size="small"
                    name="last_name"
                    value={modalData?.last_name}
                    onChange={handleChange}
                    InputProps={{
                      sx: {
                        backgroundColor: "white",
                        borderRadius: "8px",
                        padding: "5px",
                        height: "45px",
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#6d28d9",
                        },
                      },
                    }}
                  />
                </div>
              </div>
              <div className="mt-4">
                <p className="text-sm text-slate-500">Email*</p>
                <div className="mt-1">
                  <TextField
                    placeholder="email@email.com"
                    fullWidth
                    variant="outlined"
                    size="small"
                    name="email"
                    value={modalData?.email}
                    onChange={handleChange}
                    InputProps={{
                      sx: {
                        backgroundColor: "white",
                        borderRadius: "8px",
                        padding: "5px",
                        height: "45px",
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#6d28d9",
                        },
                      },
                    }}
                  />
                </div>
              </div>
              <div className="mt-4">
                <p className="text-sm text-slate-500">Permission Type*</p>
                <div className="mt-1">
                  <Select
                    fullWidth
                    displayEmpty
                    value={modalData?.permission_type}
                    name="permission_type"
                    onChange={handleChange}
                    size="small"
                    sx={{
                      borderRadius: "8px",
                      paddingY: "1px",
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#6d28d9",
                      },
                      height: "45px",
                      backgroundColor: "white",
                    }}
                  >
                    <MenuItem disabled value="">
                      Select permission
                    </MenuItem>
                    {(props.permissionRole === "company_admin" ||
                      props.permissionRole === "rightwork_internal" ||
                      modalData?.permission_type === "company_admin") && (
                      <MenuItem key="company_admin" value="company_admin">
                        Admin
                      </MenuItem>
                    )}
                    <MenuItem key="company_manager" value="company_manager">
                      Manager
                    </MenuItem>
                    <MenuItem key="company_employee" value="company_employee">
                      Employee
                    </MenuItem>
                    {props.timeEntriesEnabled && (
                      <MenuItem
                        key="company_timeclock"
                        value="company_timeclock"
                      >
                        Time Clock
                      </MenuItem>
                    )}
                  </Select>
                </div>
              </div>
              {!modalEdit && (
                <div className="mt-5">
                  <RolesCapabilitySelector
                    roleIds={roleIds}
                    mainRoleId={mainRoleId}
                    setMainRoleId={setMainRoleId}
                    handleRoleChange={handleRoleChange}
                    update_role_proficiency={update_role_proficiency}
                    getStars={getStars}
                    role_names={props.roleNames || []}
                    proficiencyStarsCount={props.proficiencyStarsCount}
                    proficiencyFromLMSEnabled={props.proficiencyFromLMSEnabled}
                  />
                </div>
              )}
              <div className="mt-4">
                <p className="text-sm text-slate-500">Phone</p>
                <div className="mt-1">
                  <TextField
                    placeholder="(555) 555-5555 or +52..."
                    fullWidth
                    variant="outlined"
                    size="small"
                    name="phone"
                    value={modalData?.phone}
                    onChange={handleChange}
                    InputProps={{
                      sx: {
                        backgroundColor: "white",
                        borderRadius: "8px",
                        padding: "5px",
                        height: "45px",
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#6d28d9",
                        },
                      },
                    }}
                  />
                </div>
              </div>
              {clockPinEnabled && (
                <div className="mt-4">
                  <p className="text-sm text-slate-500">Pin</p>
                  <div className="mt-1">
                    <TextField
                      placeholder="1234"
                      fullWidth
                      variant="outlined"
                      size="small"
                      name="clock_pin"
                      value={modalData?.clock_pin}
                      onChange={handleChange}
                      InputProps={{
                        sx: {
                          backgroundColor: "white",
                          borderRadius: "8px",
                          padding: "5px",
                          height: "45px",
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#6d28d9",
                          },
                        },
                      }}
                    />
                  </div>
                </div>
              )}
              <div className="mt-4">
                <p className="text-sm text-slate-500">Date of Birth</p>
                <div
                  className={`${
                    !showDailyDatePicker ? "border border-white" : ""
                  } flex-1 mt-1`}
                >
                  <div
                    className={`flex-1 bg-white rounded-lg text-sm ${
                      showDailyDatePicker
                        ? "border-2 border-violet-700"
                        : "border border-[#bdbdbd] hover:border-slate-900"
                    } cursor-pointer relative`}
                    ref={datePickerRef}
                  >
                    <div
                      className="w-full h-full px-3.5 py-2.5"
                      onClick={toggleDailyDatePicker}
                    >
                      <div className="w-full h-full flex items-center justify-between">
                        <p className="text-[16px]">
                          {modalData.date_of_birth
                            ? formatDateReadable(modalData.date_of_birth)
                            : "Click to select"}
                        </p>
                        <div className="text-slate-500">
                          <CalendarTodayIcon
                            style={{ fontSize: "18px", marginBottom: "2px" }}
                          />
                        </div>
                      </div>
                    </div>
                    {showDailyDatePicker && (
                      <div className="absolute bottom-12 left-0 bg-white z-10 flex items-center justify-center pl-4 pr-5 border border-[#bdbdbd] rounded-lg shadow-xl overflow-hidden">
                        <div className="mt-3">
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DemoContainer
                              components={["DateCalendar", "DateCalendar"]}
                            >
                              <DemoItem>
                                <DateCalendar
                                  value={
                                    modalData?.date_of_birth
                                      ? dayjs(modalData?.date_of_birth)
                                      : null
                                  }
                                  onChange={handleDateChange}
                                  slotProps={{
                                    day: {
                                      sx: {
                                        "&.MuiPickersDay-root.Mui-selected": {
                                          backgroundColor: "#6d28d9",
                                        },
                                      },
                                    },
                                  }}
                                />
                              </DemoItem>
                            </DemoContainer>
                          </LocalizationProvider>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="mt-4">
                <p className="text-sm text-slate-500">Date of Hire</p>
                <div
                  className={`${
                    !showDateOfHirePicker ? "border border-white" : ""
                  } flex-1 mt-1`}
                >
                  <div
                    className={`flex-1 bg-white rounded-lg text-sm ${
                      showDateOfHirePicker
                        ? "border-2 border-violet-700"
                        : "border border-[#bdbdbd] hover:border-slate-900"
                    } cursor-pointer relative`}
                    ref={dateOfHirePickerRef}
                  >
                    <div
                      className="w-full h-full px-3.5 py-2.5"
                      onClick={toggleDateOfHirePicker}
                    >
                      <div className="w-full h-full flex items-center justify-between">
                        <p className="text-[16px]">
                          {modalData.date_of_hire
                            ? formatDateReadable(modalData.date_of_hire)
                            : "Click to select"}
                        </p>
                        <div className="text-slate-500">
                          <CalendarTodayIcon
                            style={{ fontSize: "18px", marginBottom: "2px" }}
                          />
                        </div>
                      </div>
                    </div>
                    {showDateOfHirePicker && (
                      <div className="absolute bottom-12 left-0 bg-white z-10 flex items-center justify-center pl-4 pr-5 border border-[#bdbdbd] rounded-lg shadow-xl overflow-hidden">
                        <div className="mt-3">
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DemoContainer
                              components={["DateCalendar", "DateCalendar"]}
                            >
                              <DemoItem>
                                <DateCalendar
                                  value={
                                    modalData?.date_of_hire
                                      ? dayjs(modalData?.date_of_hire)
                                      : null
                                  }
                                  onChange={handleDateOfHireChange}
                                  slotProps={{
                                    day: {
                                      sx: {
                                        "&.MuiPickersDay-root.Mui-selected": {
                                          backgroundColor: "#6d28d9",
                                        },
                                      },
                                    },
                                  }}
                                />
                              </DemoItem>
                            </DemoContainer>
                          </LocalizationProvider>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {/* Emergency Contact (SB 294) */}
              <div className="mt-6">
                <p className="text-sm font-semibold text-slate-700">Emergency Contact</p>
                <div className="mt-3">
                  <p className="text-sm text-slate-500">Contact Name</p>
                  <div className="mt-1">
                    <TextField
                      placeholder="Full name"
                      fullWidth
                      variant="outlined"
                      size="small"
                      name="emergency_contact_name"
                      value={modalData?.emergency_contact_name || ""}
                      onChange={handleChange}
                      InputProps={{
                        sx: {
                          backgroundColor: "white",
                          borderRadius: "8px",
                          padding: "5px",
                          height: "45px",
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#6d28d9",
                          },
                        },
                      }}
                    />
                  </div>
                </div>
                <div className="mt-3">
                  <p className="text-sm text-slate-500">Contact Phone</p>
                  <div className="mt-1">
                    <TextField
                      placeholder="Phone number"
                      fullWidth
                      variant="outlined"
                      size="small"
                      name="emergency_contact_phone"
                      value={modalData?.emergency_contact_phone || ""}
                      onChange={handleChange}
                      InputProps={{
                        sx: {
                          backgroundColor: "white",
                          borderRadius: "8px",
                          padding: "5px",
                          height: "45px",
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#6d28d9",
                          },
                        },
                      }}
                    />
                  </div>
                </div>
                <div className="mt-3">
                  <p className="text-sm text-slate-500">Relationship</p>
                  <div className="mt-1">
                    <TextField
                      placeholder="e.g. Spouse, Parent, Sibling"
                      fullWidth
                      variant="outlined"
                      size="small"
                      name="emergency_contact_relationship"
                      value={modalData?.emergency_contact_relationship || ""}
                      onChange={handleChange}
                      InputProps={{
                        sx: {
                          backgroundColor: "white",
                          borderRadius: "8px",
                          padding: "5px",
                          height: "45px",
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#6d28d9",
                          },
                        },
                      }}
                    />
                  </div>
                </div>
              </div>
              {!modalEdit && (
                <div className="w-full mt-4">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={sendWelcomeEmail}
                        onChange={(event) =>
                          setSendWelcomeEmail(event.target.checked)
                        }
                        sx={{
                          color: "#6d28d9",
                          "&.Mui-checked": {
                            color: "#6d28d9",
                          },
                        }}
                      />
                    }
                    label="Send welcome message"
                  />
                  <p className="text-xs text-slate-500 ml-9">
                    Send login instructions now. You can also send it later from the employee row if you skip this step.
                  </p>
                  {sendWelcomeEmail && (
                    <div className="ml-9 mt-3 flex items-center space-x-4">
                      <label className="flex items-center cursor-pointer text-sm text-slate-700">
                        <input
                          type="radio"
                          name="welcome-channel"
                          value="email"
                          checked={welcomeChannel === "email"}
                          onChange={() => setWelcomeChannel("email")}
                          className="mr-2 accent-violet-700"
                        />
                        Email
                      </label>
                      <label
                        className={
                          isValidSMSPhone(modalData.phone)
                            ? "flex items-center cursor-pointer text-sm text-slate-700"
                            : "flex items-center cursor-not-allowed text-sm text-slate-400"
                        }
                      >
                        <input
                          type="radio"
                          name="welcome-channel"
                          value="sms"
                          checked={welcomeChannel === "sms"}
                          onChange={() => setWelcomeChannel("sms")}
                          disabled={!isValidSMSPhone(modalData.phone)}
                          className="mr-2 accent-violet-700"
                        />
                        SMS
                        {!isValidSMSPhone(modalData.phone) && (
                          <span className="ml-1 text-xs">
                            {modalData.phone
                              ? "(invalid phone)"
                              : "(requires phone)"}
                          </span>
                        )}
                      </label>
                    </div>
                  )}
                </div>
              )}
              {modalData?.is_hidden && (
                <div className="mt-6 w-fit">
                  <div className="border border-rose-600 bg-rose-50 text-rose-600 px-3 h-10 rounded-lg flex items-center justify-center">
                    <p>hidden</p>
                  </div>
                </div>
              )}
            </div>
            <div className="h-[200px]"></div>
          </div>
          <div>
            <DrawerFooterContainerLarge>
              <div className="w-full">
                <div className="w-full">
                  <LoadingButton
                    variant="contained"
                    // loading={savingLoading}
                    loadingPosition="end"
                    endIcon={<SaveIcon />}
                    onClick={() => handleSubmit()}
                    sx={{
                      width: "100%",
                      textTransform: "none",
                      borderRadius: "12px",
                      paddingY: "14px",
                      backgroundColor: "#6d28d9",
                      "&:hover": {
                        backgroundColor: "#5b21b6",
                      },
                      fontSize: "16px",
                    }}
                  >
                    {modalEdit ? "Save Employee" : "Add Employee"}
                  </LoadingButton>
                </div>
                <div className="w-full mt-3">
                  <ButtonOutlined text="Cancel" onClick={() => handleClose()} />
                </div>
              </div>
            </DrawerFooterContainerLarge>
          </div>
        </div>
      </Drawer>
    </>
  );
}
